import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {DynamicSearchPayload} from '@ispm-shared/modules/common/dynamic-search';
import {Injectable} from '@angular/core';
import {InterpolateService} from '@ispm/shared/services/interpolate.service';
import {IspmHttpClient} from '@ispm/core/services/ispm-http-client';
import {Observable} from 'rxjs/Observable';
import {PollJobService} from '@ispm/test/services/poll-job.service';
import {RouterManagerService} from '@ispm/core/services/router-manager.service';
import {Store} from '@ngrx/store';
import {get, set, unset} from 'lodash';
import {take} from 'rxjs/operators';
import {IKeyElement} from '../interfaces';
import {IgenomicsState} from '../../core/interfaces/genomics-state.interface';
import {TablelistDataHandlerService} from './tablelist-data-handler.service';
import {TestService} from '@ispm/dashboard/test/services/test.service';
import {WORKLIST_ACTIONS} from '../store/worklist.actions';
import {StringUtils} from "@ispm/shared/utils/String.utils";
import {WorklistHelper} from "../worklist-helper";

@Injectable()
export class WorklistService {
  public static baseUrl = '/workflow';

  public activeRowSubject: BehaviorSubject<string> = new BehaviorSubject(null);
  public cacheConfig: Map<string, string> = new Map();
  public actionUsed: boolean = false;

  private _preserveSelectedRows: any[] = [];

  constructor(
    private _ispmHttpClient: IspmHttpClient,
    private _tablelistDataHandlerService: TablelistDataHandlerService,
    private interpolateService: InterpolateService,
    private pollJobService: PollJobService,
    private routerManagerService: RouterManagerService,
    private store: Store<IgenomicsState>,
    private testService: TestService) {
    this.init();
  }

  public init(): void {
    this.store.dispatch({ type: WORKLIST_ACTIONS.INIT_TEMPLATES});
  }

  set preserveSelectedRows(value: any[]) {
    this._preserveSelectedRows = [...value];
  }

  get preserveSelectedRows(): any[] {
    return this._preserveSelectedRows;
  }

  public updateActiveRow(trackString: string): void {
    this.activeRowSubject.next(trackString);
  }

  public getActiveRowObservable(): Observable<string> {
    return this.activeRowSubject.asObservable();
  }

  public getTextFromHtml(value) {
    if (typeof value !== 'string') {
      return value;
    }

    return value ? value.replace(/<[^>]+>/gm, '') : value;
  }

  public sort(a: any, b: any, type, key: string, order: number) {
    try {
      const valueA = get(a, key) || '';
      const valueB = get(b, key) || '';

      switch (type.toLowerCase()) {
        case 'date':
          return this.sortByDate(valueA, valueB, order);
        default:
          return this.sortGeneric(valueA, valueB, order);
      }
    } catch (e) {
      return 0;
    }
  }

  public filterByText(text: string, values: Array<any> = [], confs: Array<IKeyElement> = [], useMultipleSearch: boolean = false, exactMultiSearch: boolean = false) {
    return useMultipleSearch ? this.multipleFilterByText(text, values, confs, exactMultiSearch) : this.simpleFilterByText(text, values, confs);
  }

  private simpleFilterByText(text: string, values: Array<any> = [], confs: Array<IKeyElement> = []) {
    return values.filter(val => {
      return confs.find(conf => {
        let fallbackValue = null, subConfig = null;

        if (conf.value != null && typeof conf.value !== 'object') {
          fallbackValue = conf.value;
        } else if (conf.value != null) {
          subConfig = conf.value;
          fallbackValue = conf.value.value;
        }

        const item = this.getValueByType(val, conf.key, conf.type, fallbackValue, subConfig);

        return item
          .toString()
          .toLowerCase()
          .contains(text.toLowerCase());
      });
    });
  }

  private multipleFilterByText(text: string, values: Array<any> = [], confs: Array<IKeyElement> = [], exactMultiSearch: boolean = false) {
    return exactMultiSearch ? this.exactMultipleSearch(text, values, confs) : this.cumulativeMultipleSearch(text, values, confs);
  }

  private cumulativeMultipleSearch(text: string, values: Array<any> = [], confs: Array<IKeyElement> = []) {
    // Search by chunks. Result is a sum of search for each field
    const searchChunks: string[] = StringUtils.getChunks(text);
    return values
      .filter(val => {
        const weight: { val: number } = {val: 0};
        let commonResult = false;

        confs.forEach(conf => {
          let fallbackValue = null, subConfig = null;
          const fieldWeight: { val: number } = {val: 0};

          if (conf.value != null && typeof conf.value !== 'object') {
            fallbackValue = conf.value;
          } else if (conf.value != null) {
            subConfig = conf.value;
            fallbackValue = conf.value.value;
          }
          // found field value from worklist item
          const fieldItem = this.getValueByType(val, conf.key, conf.type, fallbackValue, subConfig).toString().toLowerCase();
          const chunksResult: boolean = StringUtils.calcIsIncluded(fieldItem, searchChunks, fieldWeight, false);
          weight.val += fieldWeight.val;
          commonResult = commonResult || chunksResult;
        });
        set(val, 'weight', weight.val);
        return commonResult;
      })
      .sort((a, b) => b.weight - a.weight)
      .map(item => {
        unset(item, 'weight');
        return item;
      });
  }

  public exactMultipleSearch(text: string, values: Array<any> = [], confs: Array<IKeyElement> = []) {
    const countedChunks = StringUtils.getCountedChunks(text);
    const chunksCount = countedChunks.size;
    return values
      .filter(val => {
        for (const key of countedChunks.keys()) {
          countedChunks.set(key,  { number:0, exist: false});
        }
        const weight: { val: number } = {val: 0};
        let commonResult = false;

        confs.forEach(conf => {
          let fallbackValue = null, subConfig = null;
          if (conf.value != null && typeof conf.value !== 'object') {
            fallbackValue = conf.value;
          } else if (conf.value != null) {
            subConfig = conf.value;
            fallbackValue = conf.value.value;
          }
          // found field value from worklist item
          const fieldItem = this.getValueByType(val, conf.key, conf.type, fallbackValue, subConfig).toString().toLowerCase();
          const chunksResult: boolean = StringUtils.isExactCountedIncluded(fieldItem, countedChunks);
          commonResult = commonResult || chunksResult;
        });

        let foundChunksNumber: number = 0;
        for (const value of countedChunks.values()) {
          if (value.exist) {
            weight.val += value.number;
            foundChunksNumber++;
          }
        }
        set(val, 'weight', foundChunksNumber === chunksCount ? weight.val : 0);
        return commonResult;
      })
      .sort((a, b) => b.weight - a.weight)
      .filter(val => val.weight >= chunksCount)
      .map(item => {
        unset(item, 'weight');
        return item;
      });
  }

  public redirectToPatientProfileTest(patientId, workflowId, revisionId, emrIntegration?, defPageLoadFunRef?) {
    // get the redirected URL from the table configuration storage
    this._tablelistDataHandlerService.getConfigTemplate()
      .pipe(take(1))
      .subscribe(tableStorage => {
        if (tableStorage && tableStorage.config && tableStorage.config.redirectUrl) {
          const values = {
            rowData: {
              data: {
                patient_id: patientId,
                workflowrequest_id: workflowId,
                revision_id: revisionId
              }
            }
          };

          if (emrIntegration) {
            values['EMRIntegration'] = emrIntegration;
          }

          const redirectUrl = this.interpolateService.interpolate(tableStorage.config.redirectUrl, values);
          const url = this.routerManagerService.getURL(redirectUrl);
          const uriState = this.routerManagerService.urlToState(redirectUrl);
          this.goToRevision(url, uriState.params, revisionId, workflowId);
          // if for some reason, cannot find the redirected URL in storage - reload current page
        } else {
          console.log('cannot find the redirected URL in \'tablelist\' storage - reloading current page');

          // activate the function, if is given, to load the default page in case redirect URL is not provided
          if (defPageLoadFunRef) {
            defPageLoadFunRef();
          }
        }
      });
  }

  private sortGeneric(x, y, order) {
    if (x < y) {
      return order * -1;
    }

    if (x > y) {
      return order * 1;
    }

    return 0;
  }

  private sortByDate(date1, date2, order) {
    if (!(date1 instanceof Date)) {
      date1 = this.parseDate(date1);
    }

    if (!(date2 instanceof Date)) {
      date2 = this.parseDate(date2);
    }

    const result = date1 - date2;

    return result * order;
  }

  private parseDate(date) {
    const parsedDate = Date.parse(date);

    return isNaN(parsedDate) ? 0 : parsedDate;
  }

  private getValueByType(item: any, path: string, type: string, fallback?, subConf?) {
    let val = get(item, path, fallback) || '';

    if (!type) {
      type = '';
    }

    switch (type.toLowerCase()) {
      case 'date':
        // Filter by the presented date (and not by date & time): GEN-19465
        if (subConf && subConf.subKey) {
          val = this.getValueByType(val, subConf.subKey, subConf.subType, subConf.value);
        }

        const dateItem = new Date(val);
        val = !isNaN(dateItem.getTime()) ? (<any> dateItem).toPlatformFormat() : '';
        break;
      case 'array':
        if (val !== '' && subConf && subConf.subKey) {
          const res = val.map(value => this.getValueByType(value, subConf.subKey, subConf.subType, subConf.value));
          val = res.join(',');
        }

        break;
      default:
        val = subConf && subConf.subKey ? this.getValueByType(val, subConf.subKey, subConf.subType, subConf.value) : val;
        break;
    }
    return val;
  }

  private goToRevision(to, params, revisionId: any, workflowId: any) {
    const skipLocationChange = to && to.indexOf(WorklistHelper.PATHOLOGIST_ROUTE) > -1;
    this.pollJobService.pollingUntilStepAndNavigate(revisionId, workflowId, this.testService.testConfiguration, to, params, {
      skipLocationChange: skipLocationChange
    });
  }

  /**
   * @deprecated
   * @param resourceType
   * @param query
   */
  public getWorklist(resourceType: string, query: DynamicSearchPayload): Observable<any> {
    return this._ispmHttpClient.post('entity-tree/' + resourceType + '/dynamicSearch', query);
  }

  public getDisclaimer(organization: string, application: string): Observable<any> {
    return this._ispmHttpClient.get('analytics/getDisclaimerMessage', { organization, application}) ;
  }
}
